import * as React from 'react';
import { createStyles, Modal, Stepper, Box, Group, Button, ScrollArea, Checkbox } from '@mantine/core';
import { useTranslation, Trans } from 'react-i18next';
import { useDisclosure, useMediaQuery, useInputState } from '@mantine/hooks';
import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/browser';

// API
import { PatchUserLifecycleFlag, queryClient } from '../../api';

// Contexts
import { AuthContext } from '../../contexts/Auth';
import { RefetchSelfContext } from '../../contexts/RefetchSelf';
import { ViewingAsContext } from '../../contexts/ViewingAs';

// Components
import { LanguageSelectorRemote } from '../LanguageSelectorRemote';
import { GetHelpButton } from '../GetHelpButton';

// Hooks
import { usePermission } from '../../hooks/usePermission';

// Types
import { IUserRole, UserTypes } from '../../types';

const useStyles = createStyles(() => ({
    contentWrapper: {
        h1: {
            marginTop: 0
        },
        padding: '1rem'
    },
    modalHeader: {
        marginTop: 0
    },
    modalHeaderMobile: {
        fontSize: '1.8rem'
    },
    root: {
        padding: '1.5rem 2rem 0.5rem'
    },
    content: {
        padding: '0'
    },
    steps: {
        display: 'none'
    },
    subHeader: {
        marginBottom: 0
    },
    paragraph: {
        marginTop: '5px'
    },
    signatureParagraph: {
        marginTop: '3rem'
    },
    buttonsGroup: {
        justifyContent: 'space-between',
        padding: '2rem 0 0'
    },
    checkbox: {
        marginTop: '1.5rem',
        fontWeight: 500
    },
    checkboxError: {
        '.mantine-Checkbox-label': {
            color: '#fa5252'
        }
    },
    modalContent: {
        overflow: 'hidden'
    },
    languageSelectorBox: {
        maxWidth: '12rem'
    }
}));

interface SignAgreementProps {
    onToggle?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const SignAgreement: React.FC<SignAgreementProps> = ({ onToggle }) => {
    const viewport = React.useRef<HTMLDivElement>(null);
    const [opened, { close, open }] = useDisclosure(false);
    const { userType } = React.useContext(ViewingAsContext);
    const { authState } = React.useContext(AuthContext);
    const { refetch } = React.useContext(RefetchSelfContext);
    const { classes, cx } = useStyles();
    const { t: agreementTranslations } = useTranslation('agreements');
    const { t: commonTranslations } = useTranslation('common');
    const isMobile = useMediaQuery('(max-width: 50em)');
    const [active, setActive] = React.useState(0);
    const isPrimary = usePermission([IUserRole.ORG_PRIMARY]);
    const [checkboxError, setCheckboxError] = useInputState(false);
    const [checked, setChecked] = React.useState(false);
    const validateSignature = React.useCallback(() => {
        if (!checked) {
            setCheckboxError(true);
            return false;
        } else {
            setCheckboxError(false);
            return true;
        }
    }, [setCheckboxError, checked]);
    const nextStep = React.useCallback(() => {
        setActive((current) => (current < 3 ? current + 1 : current));
        setTimeout(() => viewport.current!.scrollTo({ top: 0, behavior: 'smooth' }));
    }, [setActive]);
    const finishAndClose = React.useCallback(() => {
        if (!checked) {
            setCheckboxError(true);
        } else {
            onToggle?.(false);
            close();
        }
    }, [active, checked, onToggle]);
    const patchHasSignedFlag = useMutation(PatchUserLifecycleFlag, {
        onSuccess: async (data, variables) => {
            // queryClient.invalidateQueries({
            //     queryKey: ['self'],
            //     exact: true,
            //     refetchType: 'active'
            // });
            refetch?.();
            nextStep();
        }
    });

    React.useEffect(() => {
        if (checked && checkboxError) {
            setCheckboxError(false);
        }
    }, [checked]);

    React.useEffect(() => {
        if (authState && !authState.initializing) {
            onToggle?.(true);
            open();
        }
    }, [authState, onToggle]);

    return (
        <Modal
            id={'signAgreementModal'}
            opened={opened}
            onClose={close}
            size={1100}
            withCloseButton={false}
            closeOnClickOutside={false}
            closeOnEscape={false}
            fullScreen={isMobile}
            trapFocus={false}
            classNames={{
                content: classes.modalContent
            }}
        >
            <ScrollArea.Autosize
                mah={isMobile ? 'calc(100dvh - 2rem)' : 'calc(100dvh - (7dvh * 2))'}
                mx={'auto'}
                viewportRef={viewport}
            >
                <Stepper
                    active={active}
                    onStepClick={setActive}
                    breakpoint="sm"
                    allowNextStepsSelect={false}
                    classNames={{
                        steps: classes.steps,
                        content: classes.content,
                        root: cx(classes.root, {
                            [classes.content]: isMobile
                        })
                    }}
                >
                    <Stepper.Step>
                        <h1
                            className={cx(classes.modalHeader, {
                                [classes.modalHeaderMobile]: isMobile
                            })}
                        >
                            {agreementTranslations('platformAgreement.step1.welcome.title')}
                        </h1>
                        {isPrimary ? <i>{agreementTranslations('platformAgreement.step1.welcome.p1')}</i> : null}
                        <h2 className={classes.subHeader}>
                            {agreementTranslations('platformAgreement.step1.whatIsBeep.title')}
                        </h2>
                        <p className={classes.paragraph}>
                            {agreementTranslations('platformAgreement.step1.whatIsBeep.p1')}
                        </p>
                        <p className={classes.paragraph}>
                            <Trans
                                t={agreementTranslations}
                                i18nKey={'platformAgreement.step1.whatIsBeep.p2'}
                                components={{
                                    privacyPolicy: (
                                        <a
                                            href={commonTranslations(`links.privacyPolicy`) ?? ''}
                                            rel="noopener noreferrer"
                                            target="_blank"
                                        />
                                    )
                                }}
                            />
                        </p>
                        <h2 className={classes.subHeader}>
                            {agreementTranslations('platformAgreement.step1.beepSurveys.title')}
                        </h2>
                        <p className={classes.paragraph}>
                            {agreementTranslations('platformAgreement.step1.beepSurveys.p1')}
                        </p>
                        <h2 className={classes.subHeader}>
                            {agreementTranslations('platformAgreement.step1.obligation.title')}
                        </h2>
                        <p className={classes.paragraph}>
                            {agreementTranslations('platformAgreement.step1.obligation.p1')}
                        </p>
                        <Group className={classes.buttonsGroup}>
                            <Box className={classes.languageSelectorBox}>
                                <LanguageSelectorRemote />
                            </Box>
                            <Button
                                size="md"
                                onClick={() => {
                                    nextStep();
                                }}
                            >
                                {commonTranslations('nextPage')}
                            </Button>
                        </Group>
                    </Stepper.Step>
                    <Stepper.Step>
                        <h2 className={classes.subHeader} style={{ marginTop: 0 }}>
                            {agreementTranslations('platformAgreement.step2.privacy.title')}
                        </h2>
                        <p className={classes.paragraph}>
                            <Trans
                                t={agreementTranslations}
                                i18nKey={'platformAgreement.step2.privacy.p1'}
                                components={{
                                    strong: <strong />,
                                    privacyPolicy: (
                                        <a
                                            href={commonTranslations(`links.privacyPolicy`) ?? ''}
                                            rel="noopener noreferrer"
                                            target="_blank"
                                        />
                                    )
                                }}
                            />
                        </p>
                        <h2 className={classes.subHeader}>
                            {agreementTranslations('platformAgreement.step2.termsConds.title')}
                        </h2>
                        <p className={classes.paragraph}>
                            <Trans
                                t={agreementTranslations}
                                i18nKey={'platformAgreement.step2.termsConds.p1'}
                                components={{
                                    strong: <strong />,
                                    termsAndConds: (
                                        <a
                                            href={commonTranslations(`links.termsAndConds`) ?? ''}
                                            rel="noopener noreferrer"
                                            target="_blank"
                                        />
                                    )
                                }}
                            />
                        </p>
                        <h2 className={classes.subHeader}>
                            {agreementTranslations('platformAgreement.step2.contact.title')}
                        </h2>
                        <p className={classes.paragraph}>
                            <Trans
                                t={agreementTranslations}
                                i18nKey={'platformAgreement.step2.contact.p1'}
                                components={{
                                    emailLink: (
                                        <a
                                            href={`mailto:${commonTranslations('links.contactEmail') ?? ''}`}
                                            rel="noopener noreferrer"
                                            target="_blank"
                                        />
                                    )
                                }}
                            />
                        </p>
                        <div className={classes.signatureParagraph}>
                            <strong>
                                <Trans
                                    t={agreementTranslations}
                                    i18nKey={'platformAgreement.step2.signature.p1'}
                                    components={{
                                        privacyPolicy: (
                                            <a
                                                href={commonTranslations(`links.privacyPolicy`) ?? ''}
                                                rel="noopener noreferrer"
                                                target="_blank"
                                            />
                                        ),
                                        termsAndConds: (
                                            <a
                                                href={commonTranslations(`links.termsAndConds`) ?? ''}
                                                rel="noopener noreferrer"
                                                target="_blank"
                                            />
                                        )
                                    }}
                                />
                            </strong>
                            <Checkbox
                                checked={checked}
                                error={checkboxError}
                                className={cx(classes.checkbox, {
                                    [classes.checkboxError]: checkboxError
                                })}
                                onChange={(event) => setChecked(event.currentTarget.checked)}
                                label={
                                    <Trans
                                        t={agreementTranslations}
                                        i18nKey={'platformAgreement.step2.signature.checkboxLabel'}
                                        components={{
                                            privacyPolicy: (
                                                <a
                                                    href={commonTranslations(`links.privacyPolicy`) ?? ''}
                                                    rel="noopener noreferrer"
                                                    target="_blank"
                                                />
                                            ),
                                            termsAndConds: (
                                                <a
                                                    href={commonTranslations(`links.termsAndConds`) ?? ''}
                                                    rel="noopener noreferrer"
                                                    target="_blank"
                                                />
                                            )
                                        }}
                                    />
                                }
                            />
                        </div>
                        <Group className={classes.buttonsGroup}>
                            {/* <Group position="right"> */}
                            <Box className={classes.languageSelectorBox}>
                                <LanguageSelectorRemote />
                            </Box>
                            {/* </Group> */}
                            <Button
                                size="md"
                                onClick={async () => {
                                    try {
                                        if (validateSignature() && authState?._id) {
                                            await patchHasSignedFlag.mutate({
                                                userId: authState._id,
                                                flag: 'hasSignedAgreement'
                                            });
                                        }
                                    } catch (e) {
                                        Sentry.captureException(e);
                                    }
                                }}
                            >
                                {commonTranslations('iAgree')}
                            </Button>
                        </Group>
                    </Stepper.Step>
                    <Stepper.Step>
                        <h2 className={classes.subHeader}>{agreementTranslations('platformAgreement.step3.title')}</h2>
                        <p>
                            {agreementTranslations(
                                isPrimary
                                    ? 'platformAgreement.step3.p1.collabolator'
                                    : 'platformAgreement.step3.p1.nonCollabolator'
                            )}
                        </p>
                        {userType === UserTypes.AUTHENTICATED && isPrimary ? (
                            <>
                                <Group className={classes.buttonsGroup}>
                                    <Box className={classes.languageSelectorBox}>
                                        <LanguageSelectorRemote />
                                    </Box>
                                    <Button size="md" onClick={finishAndClose}>
                                        {commonTranslations('startOnboarding')}
                                    </Button>
                                </Group>
                            </>
                        ) : (
                            <Group className={classes.buttonsGroup}>
                                <Box className={classes.languageSelectorBox}>
                                    <LanguageSelectorRemote />
                                </Box>
                                <Button size="md" onClick={finishAndClose}>
                                    {commonTranslations('finish')}
                                </Button>
                            </Group>
                        )}
                    </Stepper.Step>
                </Stepper>
            </ScrollArea.Autosize>
            <GetHelpButton none />
        </Modal>
    );
};
